import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { GridColumnGroupingModel, GridColDef } from '@mui/x-data-grid-premium'

import { prepareAuthenticatedHeaders } from './utils/headers'
import type {
  Events,
  SubEvents,
  Sessions,
  RegistrationDetails,
  PageOptions,
  Participants,
  Pagination,
  OptionsParticipants,
  ParticipantStatus,
  ParticipantOptionStatus,
  Options,
  Abstracts,
  AbstractStatus,
  AbstractReviews,
  AbstractsStatistics,
  Figures,
  Sponsorship,
  SponsorshipStats,
} from '../types'
import { convertToFile } from '../utils'

export const DEFAULT_PAGE_SIZE = 500

export const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/api/v1/events`,
    prepareHeaders: prepareAuthenticatedHeaders,
  }),
  tagTypes: ['Events', 'AbstractsSelection'],
  endpoints: builder => ({
    listEvents: builder.query<Events[], void>({
      query: () => ({
        url: '/',
      }),
    }),
    getEvent: builder.query<Events, number>({
      query: id => ({
        url: `/${id}/`,
      }),
    }),
    getEventRegistrationDetails: builder.query<RegistrationDetails, number>({
      query: id => ({
        url: `/${id}/registration_details/`,
      }),
    }),
    getSession: builder.query<Sessions, GetSession>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/sessions/${id}/`,
      }),
    }),
    listSessions: builder.query<Sessions[], number>({
      query: id => ({
        url: `/${id}/sessions/`,
      }),
    }),
    getSubEvent: builder.query<SubEvents, GetSubEvent>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/sub-events/${id}/`,
      }),
    }),
    listSubEvents: builder.query<SubEvents[], ListSubEvents>({
      query: ({ id, registrationStatus }) => ({
        url: `/${id}/sub-events/${registrationStatus ? `?registration_status=${registrationStatus}` : ''}`,
      }),
    }),
    createEvent: builder.mutation<Events, CreateEvent>({
      query: data => ({
        url: '/',
        method: 'POST',
        body: data,
      }),
    }),
    createSession: builder.mutation<Sessions, CreateSession>({
      query: ({ eventId, session }) => ({
        url: `/${eventId}/sessions/`,
        method: 'POST',
        body: session,
      }),
    }),
    createSubEvent: builder.mutation<SubEvents, CreateSubEvent>({
      query: ({ eventId, subEvent }) => ({
        url: `/${eventId}/sub-events/`,
        method: 'POST',
        body: subEvent,
      }),
    }),
    deleteEvent: builder.mutation<void, number>({
      query: id => ({
        url: `/${id}/`,
        method: 'DELETE',
      }),
    }),
    modifyEvent: builder.mutation<Events, ModifyEvent>({
      query: ({ id, data }) => ({
        url: `/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    modifySession: builder.mutation<Sessions, ModifySession>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/sessions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    modifySubEvent: builder.mutation<SubEvents, ModifySubEvent>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/sub-events/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteSession: builder.mutation<void, DeleteSession>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/sessions/${id}/`,
        method: 'DELETE',
      }),
    }),
    deleteSubEvent: builder.mutation<void, DeleteSubEvent>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/sub-events/${id}/`,
        method: 'DELETE',
      }),
    }),
    listOptions: builder.query<Options[], number>({
      query: eventId => ({
        url: `/${eventId}/options/`,
      }),
    }),
    getOption: builder.query<Options[], GetOption>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/options/${id}/`,
      }),
    }),
    createOption: builder.mutation<Options, CreateOption>({
      query: ({ eventId, option }) => ({
        url: `/${eventId}/options/`,
        method: 'POST',
        body: option,
      }),
    }),
    modifyOption: builder.mutation<Options, ModifyOption>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/options/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteOption: builder.mutation<void, DeleteOption>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/options/${id}/`,
        method: 'DELETE',
      }),
    }),
    myParticipation: builder.query<Participants, number>({
      query: eventId => ({
        url: `/${eventId}/participants/me/`,
      }),
    }),
    myAgenda: builder.query<MyAgenda, number>({
      query: eventId => ({
        url: `/${eventId}/participants/my-agenda/`,
      }),
    }),
    listParticipants: builder.query<ResponseListParticipants, ListParticipants>({
      query: ({ eventId, page = 1, pageSize = DEFAULT_PAGE_SIZE, status }) => ({
        url: `/${eventId}/participants/?page=${page}&page_size=${pageSize}${status ? `&status=${status}` : ''}`,
      }),
    }),
    getParticipant: builder.query<Participants<true>, GetParticipant>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/participants/${id}/`,
      }),
    }),
    patchParticipant: builder.mutation<Participants<true>, PatchParticipant>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/participants/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    addParticipant: builder.mutation<Participants<true>, AddParticipant>({
      query: ({ eventId, data }) => ({
        url: `/${eventId}/participants/`,
        method: 'POST',
        body: data,
      }),
    }),
    cancelParticipation: builder.mutation<Participants<true>, CancelParticipant>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/participants/${id}/cancel/`,
        method: 'POST',
      }),
    }),
    listOptionsParticipants: builder.query<ResponseListOptsParticipants, ListOptionsParticipants>({
      query: ({ eventId, optionId, page = 1, pageSize = DEFAULT_PAGE_SIZE, status }) => ({
        url: `/${eventId}/participant-options/?page=${page}&page_size=${pageSize}${optionId !== undefined ? `&option=${optionId}` : ''}${status ? `&status=${status}` : ''}`,
      }),
    }),
    addOptionParticipation: builder.mutation<void, AddOptionParticipation>({
      query: ({ eventId, data }) => ({
        url: `/${eventId}/participant-options/`,
        method: 'POST',
        body: data,
      }),
    }),
    modifyOptionParticipation: builder.mutation<void, PatchOptionParticipation>({
      query: ({ eventId, participationId, data }) => ({
        url: `/${eventId}/participant-options/${participationId}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteOptionParticipation: builder.mutation<void, DeleteOptionParticipation>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/participant-options/${id}/`,
        method: 'DELETE',
      }),
    }),
    getHotelOverview: builder.query<Overview, number>({
      query: eventId => ({
        url: `/${eventId}/hotel_overview/`,
      }),
    }),
    getMealOverview: builder.query<Overview, number>({
      query: eventId => ({
        url: `/${eventId}/meal_overview/`,
      }),
    }),
    getContributorOverview: builder.query<Overview, number>({
      query: eventId => ({
        url: `/${eventId}/contributors/`,
      }),
    }),
    getQRCode: builder.query<Blob, number>({
      query: id => ({
        url: `/${id}/qr_code/`,
        responseHandler: async response => {
          if (!response.ok) {
            return response.json()
          }
          const url = window.URL.createObjectURL(await response.blob())
          window.open(url, `sfcftc-event/${id}/qr_code/`)
          return url
        },
        cache: 'no-cache',
      }),
    }),
    getPdfProgram: builder.query<Blob, number>({
      query: id => ({
        url: `/${id}/program/`,
        responseHandler: async response => {
          if (!response.ok) {
            return response.json()
          }
          const url = window.URL.createObjectURL(await response.blob())
          window.open(url, `sfcftc-event/${id}/program/`)
          return url
        },
        cache: 'no-cache',
      }),
    }),
    getSubEventQRCode: builder.query<Blob, GetSubEventQRCode>({
      query: ({ eventId, subEventId }) => ({
        url: `/${eventId}/sub-events/${subEventId}/qr_code/`,
        responseHandler: async response => {
          const url = window.URL.createObjectURL(await response.blob())
          window.open(url, `sfcftc-event/${eventId}/sub-events/${subEventId}/qr_code/`)
          return url
        },
        cache: 'no-cache',
      }),
    }),
    eventCheckin: builder.mutation<void, number>({
      query: id => ({
        url: `/${id}/checkin/`,
        method: 'POST',
      }),
    }),
    subEventCheckin: builder.mutation<void, SubEventCheckin>({
      query: ({ eventId, subEventId }) => ({
        url: `/${eventId}/sub-events/${subEventId}/checkin/`,
        method: 'POST',
      }),
    }),
    getCertificate: builder.query<Blob, GetCerticate>({
      query: ({ eventId, participantId }) => ({
        url: `/${eventId}/participants/${participantId}/certificate/`,
        responseHandler: async response => {
          const url = window.URL.createObjectURL(await response.blob())
          window.open(url, `sfcftc-event/${eventId}/certificate/${participantId}`)
          return url
        },
        cache: 'no-cache',
      }),
    }),
    getCertificateOption: builder.query<Blob, GetCerticateOption>({
      query: ({ eventId, participantOptionId }) => ({
        url: `/${eventId}/participant-options/${participantOptionId}/certificate/`,
        responseHandler: async response => {
          const url = window.URL.createObjectURL(await response.blob())
          window.open(url, `sfcftc-event/${eventId}/certificate-option/${participantOptionId}`)
          return url
        },
        cache: 'no-cache',
      }),
    }),
    getAbstractsStatistics: builder.query<AbstractsStatistics, number>({
      query: eventId => ({
        url: `/${eventId}/abstracts-stats/`,
      }),
    }),
    listAbstracts: builder.query<ResponseListAbstracts, ListAbstracts>({
      query: ({ eventId, page = 1, pageSize = DEFAULT_PAGE_SIZE, status }) => ({
        url: `/${eventId}/abstracts/?page=${page}&page_size=${pageSize}${status ? `&status=${status}` : ''}`,
      }),
    }),
    addAbstract: builder.mutation<Abstracts, AddAbstract>({
      query: ({ eventId, data }) => ({
        url: `/${eventId}/abstracts/`,
        method: 'POST',
        body: data,
      }),
    }),
    getAbstract: builder.query<Abstracts, GetAbstract>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts/${id}/`,
      }),
    }),
    modifyAbstract: builder.mutation<Abstracts, ModifyAbstract>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/abstracts/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteAbstract: builder.mutation<void, GetAbstract>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts/${id}/`,
        method: 'DELETE',
      }),
    }),
    submitAbstract: builder.mutation<void, GetAbstract>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts/${id}/submit/`,
        method: 'POST',
      }),
    }),
    listAbstractReviews: builder.query<ResponseListAbstractReviews, ListAbstractReviews>({
      query: ({ eventId, page = 1, pageSize = DEFAULT_PAGE_SIZE, domain, theme, orderBy }) => ({
        url: `/${eventId}/abstracts-review/?page=${page}&page_size=${pageSize}${domain ? `&domain=${domain}` : ''}${theme ? `&theme=${theme}` : ''}${orderBy ? `&order_by=${orderBy}` : ''}`,
      }),
    }),
    getAbstractDetailsForReview: builder.query<Abstracts, GetAbstract>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts-review/${id}/`,
      }),
    }),
    getAbstractReview: builder.query<AbstractReviews, GetAbstract>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts-review/${id}/get-review/`,
      }),
    }),
    reviewAbstract: builder.mutation<AbstractReviews, ReviewAbstract>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/abstracts-review/${id}/review/`,
        method: 'POST',
        body: data,
      }),
    }),
    modifyAbstractReview: builder.mutation<AbstractReviews, ReviewAbstract>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/abstracts-review/${id}/update-review/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    addAbstractFigure: builder.mutation<Figures, AddAbstractFigure>({
      query: ({ eventId, abstractId, data }) => {
        const formData = new FormData()
        const blob = convertToFile(data.image)
        formData.append('image', blob, data.originalName ?? data.title)
        formData.append('title', data.title)

        return {
          url: `/${eventId}/abstracts/${abstractId}/figures/`,
          method: 'POST',
          formData: true,
          body: formData,
        }
      },
    }),
    modifyAbstractFigure: builder.mutation<Figures, ModifyAbstractFigure>({
      query: ({ eventId, abstractId, id, title, image }) => {
        const formData = new FormData()
        if (title) formData.append('title', title)
        if (image) {
          const blob = convertToFile(image)
          formData.append('image', blob, title)
        }

        return {
          url: `/${eventId}/abstracts/${abstractId}/figures/${id}/`,
          method: 'PATCH',
          formData: true,
          body: formData,
        }
      },
    }),
    deleteAbstractFigure: builder.mutation<void, DeleteAbstractFigure>({
      query: ({ eventId, abstractId, id }) => ({
        url: `/${eventId}/abstracts/${abstractId}/figures/${id}/`,
        method: 'DELETE',
      }),
    }),
    listAbstractsSelection: builder.query<ResponseListAbstractsSelection, ListAbstractsSelection>({
      query: ({
        eventId,
        page = 1,
        pageSize = DEFAULT_PAGE_SIZE,
        orderBy,
        domain,
        theme,
        numberOfRatingsGte,
        numberOfRatingsLte,
        ratingAverageGte,
        ratingAverageLte,
      }) => ({
        url: `/${eventId}/abstracts-selection/?page=${page}&page_size=${pageSize}${orderBy ? `&order_by=${orderBy}` : ''}${orderBy ? `&domain=${domain}` : ''}${theme ? `&theme=${theme}` : ''}${numberOfRatingsGte ? `&number_of_ratings_gte=${numberOfRatingsGte}` : ''}${numberOfRatingsLte ? `&number_of_ratings_lte=${numberOfRatingsLte}` : ''}${ratingAverageGte ? `&rating_average_gte=${ratingAverageGte}` : ''}${ratingAverageLte ? `&rating_average_lte=${ratingAverageLte}` : ''}`,
      }),
      // providesTags: ['AbstractsSelection'],
    }),
    modifySelectionAbstract: builder.mutation<Abstracts<true>, ModifySelectionAbstract>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/abstracts-selection/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      // invalidatesTags: ['AbstractsSelection'],
    }),
    orderUpSelectionAbstract: builder.mutation<Abstracts<true>, MoveAbstractSelection>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts-selection/${id}/up/`,
        method: 'PATCH',
      }),
      // invalidatesTags: ['AbstractsSelection'],
    }),
    orderDownSelectionAbstract: builder.mutation<Abstracts<true>, MoveAbstractSelection>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/abstracts-selection/${id}/down/`,
        method: 'PATCH',
      }),
      // invalidatesTags: ['AbstractsSelection'],
    }),
    sponsorshipStats: builder.query<SponsorshipStats, number>({
      query: eventId => ({
        url: `/${eventId}/sponsorships-stats/`,
      }),
    }),
    listSponsorships: builder.query<Pagination<Sponsorship<true>>, number>({
      query: eventId => ({
        url: `/${eventId}/sponsorships/`,
      }),
    }),
    mySponsorship: builder.query<Sponsorship, number>({
      query: eventId => ({
        url: `/${eventId}/sponsorships/my-sponsorship/`,
      }),
    }),
    getSponsorship: builder.query<Sponsorship<true>, GetSponsorship>({
      query: ({ eventId, id }) => ({
        url: `/${eventId}/sponsorships/${id}/`,
      }),
    }),
    submitSponsorship: builder.mutation<Sponsorship, SubmitSponsorship>({
      query: ({ eventId, data }) => ({
        url: `/${eventId}/sponsorships/`,
        method: 'POST',
        body: data,
      }),
    }),
    selectSponsorship: builder.mutation<Sponsorship<true>, SelectSponsorship>({
      query: ({ eventId, id, data }) => ({
        url: `/${eventId}/sponsorships/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
  }),
})

interface MyAgenda {
  sub_events: SubEvents[]
  sessions: Sessions[]
}

interface GetSponsorship {
  eventId: number
  id: number
}

export interface SubmitSponsorship {
  eventId: number
  data: Omit<Sponsorship, 'status'>
}

export interface SelectSponsorship {
  eventId: number
  id: number
  data: Pick<Sponsorship<true>, 'status' | 'laboratory'>
}

interface AddAbstractFigure {
  eventId: number
  abstractId: number
  data: Figures & { originalName?: string }
}
interface ModifyAbstractFigure {
  eventId: number
  abstractId: number
  id: number
  title?: string
  image?: string
}
interface DeleteAbstractFigure {
  eventId: number
  abstractId: number
  id: number
}
type CreateEvent = Omit<Events, 'id'>
interface CreateSession {
  eventId: number
  session: Omit<Sessions, 'id' | 'event'>
}
interface CreateSubEvent {
  eventId: number
  subEvent: Omit<SubEvents, 'id' | 'event'>
}
interface GetSubEvent {
  eventId: number
  id: number
}
interface ListSubEvents {
  id: number
  registrationStatus?: ParticipantStatus | 'any' | null | undefined
}
interface GetSession {
  eventId: number
  id: number
}
interface ModifyEvent {
  id: number
  data: Events
}
interface ModifySession {
  id: number
  eventId: number
  data: Omit<Sessions, 'id' | 'event'>
}
interface ModifySubEvent {
  id: number
  eventId: number
  data: Omit<SubEvents, 'id' | 'event'>
}
interface GetOption {
  eventId: number
  id: number
}
interface CreateOption {
  eventId: number
  option: Omit<Options, 'id'>
}
interface ModifyOption {
  id: number
  eventId: number
  data: Omit<Options, 'id' | 'type'>
}
interface ListParticipants extends PageOptions {
  eventId: number
  status?: ParticipantStatus | null
}
interface GetParticipant {
  eventId: number
  id: number
}
interface PatchParticipant {
  eventId: number
  id: number
  data: Pick<Participants, 'status' | 'present'>
}
interface AddParticipant {
  eventId: number
  data: Pick<Participants, 'status'> & {
    user: number
  }
}
interface CancelParticipant {
  eventId: number
  id: number
}
interface GetCerticate {
  eventId: number
  participantId: number
}
interface GetCerticateOption {
  eventId: number
  participantOptionId: number
}
type PatchOptionsParticipantsKeys = 'status' | 'present' | 'hotel' | 'value'
type ResponseListParticipants = Pagination<Participants<true>>
type ResponseListOptsParticipants = Pagination<OptionsParticipants<true>>
interface PatchOptionParticipation {
  eventId: number
  participationId: number
  data: Pick<OptionsParticipants, PatchOptionsParticipantsKeys>
}
interface AddOptionParticipation {
  eventId: number
  data: Pick<OptionsParticipants, PatchOptionsParticipantsKeys> & {
    participant: number
    option: number
  }
}
interface DeleteOptionParticipation {
  eventId: number
  id: number
}
export interface Overview {
  columns: (GridColDef & {
    dbField: string
  })[]
  column_groups: GridColumnGroupingModel
  rows: {
    [key: string]: unknown
    participant_id: number
    first_name: string
    last_name: string
    email: string
  }[]
}
interface GetSubEventQRCode {
  eventId: number
  subEventId: number
}
interface SubEventCheckin {
  eventId: number
  subEventId: number
}
interface DeleteSession {
  eventId: number
  id: number
}
interface DeleteSubEvent {
  eventId: number
  id: number
}
interface DeleteOption {
  eventId: number
  id: number
}
interface ListOptionsParticipants extends PageOptions {
  eventId: number
  optionId?: number
  status?: ParticipantOptionStatus | null
}
interface ListAbstractsSelection extends PageOptions {
  eventId: number
  orderBy?:
    | '-created'
    | '-domain'
    | '-modified'
    | '-number_of_ratings'
    | '-rating_average'
    | '-theme'
    | 'created'
    | 'domain'
    | 'modified'
    | 'number_of_ratings'
    | 'rating_average'
    | 'theme'
  domain?: number
  theme?: number
  numberOfRatingsGte?: number
  numberOfRatingsLte?: number
  ratingAverageGte?: number
  ratingAverageLte?: number
}
type ResponseListAbstractsSelection = Pagination<Abstracts<true>>
interface ListAbstracts extends PageOptions {
  eventId: number
  status?: AbstractStatus | null
}
type ResponseListAbstracts = Pagination<Abstracts>
interface AddAbstract {
  eventId: number
  data: Omit<Abstracts, 'owner' | 'id'>
}
interface GetAbstract {
  eventId: number
  id: number
}
interface ModifyAbstract {
  eventId: number
  id: number
  data: Omit<Abstracts, 'owner' | 'id'>
}
type OrderBy =
  | '-created'
  | '-domain'
  | '-modified'
  | '-theme'
  | 'created'
  | 'domain'
  | 'modified'
  | 'theme'
export interface ListAbstractReviews extends PageOptions {
  eventId: number
  domain?: number | null
  theme?: number | null
  orderBy?: OrderBy | null
}
type ResponseListAbstractReviews = Pagination<
  Abstracts & {
    number_of_ratings: number
    review: AbstractReviews
  }
>
interface ReviewAbstract {
  eventId: number
  id: number
  data: PostAbstractReviews
}
export type PostAbstractReviews = Pick<
  AbstractReviews,
  'conflict_of_interest' | 'score' | 'proposed_topic_session' | 'comment'
>
interface ModifySelectionAbstract {
  eventId: number
  id: number
  data: Partial<
    Pick<
      Abstracts<true>,
      'authors' | 'selection_status' | 'session' | 'order' | 'presentation_time' | 'qa_time'
    >
  >
}
interface MoveAbstractSelection {
  eventId: number
  id: number
}

export const {
  useListEventsQuery,
  useGetEventQuery,
  useGetEventRegistrationDetailsQuery,
  useGetSessionQuery,
  useListSessionsQuery,
  useGetSubEventQuery,
  useListSubEventsQuery,
  useCreateEventMutation,
  useCreateSessionMutation,
  useCreateSubEventMutation,
  useDeleteEventMutation,
  useModifyEventMutation,
  useModifySessionMutation,
  useModifySubEventMutation,
  useMyParticipationQuery,
  useCancelParticipationMutation,
  usePatchParticipantMutation,
  useListParticipantsQuery,
  useListOptionsParticipantsQuery,
  useGetParticipantQuery,
  useLazyGetParticipantQuery,
  useModifyOptionParticipationMutation,
  useAddOptionParticipationMutation,
  useDeleteOptionParticipationMutation,
  useGetHotelOverviewQuery,
  useGetMealOverviewQuery,
  useGetQRCodeQuery,
  useLazyGetQRCodeQuery,
  useGetPdfProgramQuery,
  useLazyGetPdfProgramQuery,
  useGetSubEventQRCodeQuery,
  useLazyGetSubEventQRCodeQuery,
  useEventCheckinMutation,
  useSubEventCheckinMutation,
  useGetCertificateQuery,
  useLazyGetCertificateQuery,
  useLazyGetCertificateOptionQuery,
  useGetCertificateOptionQuery,
  useListOptionsQuery,
  useGetOptionQuery,
  useCreateOptionMutation,
  useModifyOptionMutation,
  useDeleteOptionMutation,
  useDeleteSessionMutation,
  useDeleteSubEventMutation,
  useAddAbstractMutation,
  useGetAbstractQuery,
  useModifyAbstractMutation,
  useDeleteAbstractMutation,
  useSubmitAbstractMutation,
  useListAbstractsSelectionQuery,
  useListAbstractsQuery,
  useListAbstractReviewsQuery,
  useGetAbstractDetailsForReviewQuery,
  useReviewAbstractMutation,
  useModifyAbstractReviewMutation,
  useAddAbstractFigureMutation,
  useModifyAbstractFigureMutation,
  useDeleteAbstractFigureMutation,
  useGetAbstractReviewQuery,
  useGetAbstractsStatisticsQuery,
  useModifySelectionAbstractMutation,
  useOrderDownSelectionAbstractMutation,
  useOrderUpSelectionAbstractMutation,
  useMySponsorshipQuery,
} = eventsApi
